/**
 * RBAC v2 — taxonomie des permissions, unique système de droits.
 *
 * Modélisation explicite « à la CASL » : un unique objet général `rbacV2Abilities` liste chaque
 * ressource (clé concaténée `module.sous-onglet.fonctionnalité`) → tableau EXACT de ses actions.
 * On connaît ainsi précisément, au type-level, quelles actions existent sur chaque ressource.
 *
 * - Les actions partagées (`view/add/edit/delete/export/import`) existent sur plusieurs ressources.
 * - Chaque action spéciale est une action nommée à part entière, portée par UNE seule ressource.
 *
 * La clé de ressource est stockée telle quelle dans `role_permissions.resource`, et l'action dans
 * `role_permissions.permission_type`. Le périmètre (`scope`) vaut `NULL`, soit `global`, sauf sur
 * le foncier (`RBAC_V2_SCOPED_PERMISSIONS`, « Prez projet »).
 */

import type { HrPopulation } from './rbac.population.js'

/** Actions partagées par plusieurs ressources (colonnes fixes de la matrice). */
export const RBAC_V2_SHARED_ACTIONS = ['read', 'add', 'edit', 'delete', 'export', 'import'] as const

/**
 * Actions spéciales : chacune n'est portée que par la ressource à laquelle elle est associée.
 * (Colonne « Actions spécifiques » de la matrice — une ressource peut en porter plusieurs, cf.
 * `getRbacV2SpecialActions`.)
 */
export const RBAC_V2_SPECIAL_ACTIONS = [
  'launch-reappro-simulation',
  'send-ban-email',
  'assign-bo-role',
  'validate-inventory',
  'validate-defective-batch',
  'sync-schedule',
  'restart-kiosk',
  'enter-support-mode',
  'launch-sales-calculation',
  'validate-clocking',
  'reopen-clocking',
  'add-manual-clocking',
  'cancel-clocking',
  'correct-outside-window',
  'resend-unread-sms',
  'archive-real-estate-project',
  'start-intervention',
  'edit-prices',
  // Ajoutées par l'audit du 19/09.
  'publish-planning',
  'block-lot',
  'open-door',
  'restart-camera-runtime',
  'sync-cameras',
  'run-job',
  'reset-password',
  'issue-for-others',
  'release-pre-authorization',
  'activate-driver',
  'read-driver-document',
  'launch-simulation',
  'validate-tour',
] as const

export const RBAC_V2_ACTIONS = [...RBAC_V2_SHARED_ACTIONS, ...RBAC_V2_SPECIAL_ACTIONS] as const

export type RbacV2SharedAction = (typeof RBAC_V2_SHARED_ACTIONS)[number]
export type RbacV2SpecialAction = (typeof RBAC_V2_SPECIAL_ACTIONS)[number]
export type RbacV2Action = RbacV2SharedAction | RbacV2SpecialAction

const FULL = ['read', 'add', 'edit', 'delete', 'export', 'import'] as const

/**
 * OBJET GÉNÉRAL — source de vérité de la taxonomie v2.
 * Clé = `module.sous-onglet.fonctionnalité` (= valeur stockée dans `role_permissions.resource`).
 * Valeur = tableau exact des actions disponibles pour cette ressource.
 * Un tableau vide = fonctionnalité présente dans la carte mais sans action configurable (module désactivé).
 */
export const rbacV2Abilities = {
  // 1. Tableau de bord — module migré : l'écran est en lecture seule, seuls ses exports XLSX
  // (ventes, livraisons, audit, audit hebdomadaire) ajoutent une action.
  'dashboard.dashboard.global-indicators': ['read', 'export'],

  // 2. Aujourd'hui — module migré : écran de consultation seul, aucune mutation ni export.
  'today.today.daily-tracking': ['read'],

  // 3. Paniers — module migré : liste, détail et fiche client sont en lecture seule ; seul
  // l'export des tickets ajoute une action. Aucune affordance de création/édition/suppression.
  'carts.carts.cart': ['read', 'export'],
  // La vérification visuelle est un second écran du module : on consulte les contrôles produits
  // par la reconnaissance d'image et leurs statistiques, et `edit` couvre leur seul arbitrage
  // manuel (confirmer ou infirmer un contrôle). Rien ne s'y crée, ne s'y supprime ni ne s'exporte.
  'carts.vision-checks.check': ['read', 'edit'],
  // Outillage CPI (aucun écran) : lecture des EPC de test, calcul d'un panier de test et paiement
  // forcé d'un panier de test. `test-pay-cart` écrit réellement un panier validé : la permission
  // s'ajoute à une garde d'environnement hors production, elle ne la remplace pas.
  'carts.cpi-tests.test': ['read', 'add', 'edit'],

  // 4. Catégories — module migré : chaque action listée correspond à une affordance réelle
  'categories.categories.standard': ['read', 'add', 'edit', 'delete'],
  'categories.internal-categories.internal': ['read', 'add', 'edit', 'delete', 'export'],
  'categories.reappro.category': ['read', 'add', 'edit', 'delete'],
  'categories.reappro.config': ['read', 'edit', 'delete', 'export', 'import'],
  'categories.reappro.simulation': ['export', 'import', 'launch-reappro-simulation'],
  // Audit du 19/09 : les ajustements de besoin (écran « Réappro live · besoins ») et les paramètres
  // réseau du moteur (délais entre livraisons par algorithme) sortaient tous deux de `config`.
  'categories.reappro.adjustment': ['read', 'edit', 'delete'],
  'categories.reappro.algorithm': ['read', 'edit'],
  'categories.reappro.forecast-pilot': ['read', 'edit'],
  'categories.brands.brand': ['read', 'add', 'edit', 'delete'],

  // 5. Produits
  'products.products.product': [...FULL, 'edit-prices'],

  // 6. Stock Plateforme
  'platform-stock.platform-stock.stock': FULL,

  // 7. Utilisateurs — module migré : chaque action listée correspond à une affordance réelle.
  // L'onglet « Étudiants » n'existe plus dans l'app : sa ressource a été retirée.
  // `export` : les exports CSV des écrans Clients et Clients bannis (jusqu'à 5 000 lignes avec
  // email et téléphone). Audit du 02/10/2026 : ils ne demandaient que la lecture. Classeur client :
  // « Exporter » est coché sur « Utilisateur de l'app » pour exactement les rôles qui le modifient ;
  // la migration `grantCustomerExportPermission` et la conversion v1→v2 (`users:edit`) appliquent
  // la même règle (`edit` ⇒ `export`) aux rôles existants.
  'users.app-users.user': ['read', 'edit', 'delete', 'export'],
  'users.app-users.ban': ['edit', 'send-ban-email'],
  'users.app-users.solvency': ['read'],
  'users.payment-defaults.default': ['read'],
  // Liens de paiement (kanban #870) : `read` = consulter les liens envoyés et leur statut (journal
  // de la fiche client, colonne « dernière relance » des clients bannis), `add` = envoyer un lien
  // de paiement — renvoyer et annuler un lien relèvent du même droit que l'envoi. Sous-onglet
  // propre, attribuable seul : l'action touche à l'argent et ne découle pas de la lecture des
  // clients. Décision de Baptiste du 26/09/2026 : qui lit les défauts de paiement
  // (`read users.payment-defaults.default`) consulte et envoie les liens (`read` + `add`), le
  // recouvrement d'un défaut passant par l'envoi d'un lien — dérivation du manifeste, migration
  // `grantPaymentLinkPermissions` et conversion v1→v2 (`users:read`) appliquent la même règle.
  'users.payment-links.link': ['read', 'add'],
  // `assign-bo-role` : attribuer des droits au compte. Créer ou modifier une fiche reste possible
  // sans elle — elle ne conditionne que le choix des droits.
  // `reset-password` : réécrire le mot de passe d'un autre compte BO (prise de contrôle possible,
  // audit du 19/09).
  'users.bo-users.user': ['read', 'add', 'edit', 'delete', 'assign-bo-role', 'reset-password'],
  // Distinct de la fiche : une clé ouvre un accès programmatique à l'API avec les droits du compte.
  // `edit` = élargir les abilities machine d'une clé existante (jamais les restreindre).
  // `issue-for-others` : émettre une clé sur un compte qui n'est pas le sien — la clé porte les
  // droits de ce compte, c'est donc un accès équivalent (audit du 19/09).
  'users.bo-users.api-key': ['read', 'add', 'edit', 'delete', 'issue-for-others'],

  // 8. Campagne promo — module migré : pas d'export/import de campagnes dans l'app. Les codes à
  // usage unique n'ont pas d'écran propre : les générer EST leur seule création, d'où `add` sans
  // action spéciale dédiée.
  'promo.temporary-campaigns.campaign': ['read', 'add', 'edit', 'delete'],
  'promo.temporary-campaigns.single-use-codes': ['add', 'export'],

  // 9. Partenariats CE
  'works-council.works-council.partnership': ['read', 'add', 'edit', 'delete'],

  // 10. Notifications — module migré : la liste et le détail sont en lecture seule, et la seule
  // mutation est l'envoi (le formulaire « Créer une notification »). Une notification envoyée n'est
  // ni modifiable ni supprimable, et l'écran n'offre ni export ni import.
  'notifications.notifications.notification': ['read', 'add'],
  // Dupliquer, c'est renvoyer une notification pré-remplie depuis une existante : sa seule action
  // est donc l'envoi, distinct de la création à partir de zéro.
  'notifications.notifications.duplication': ['add'],

  // 11. Boutiques — module migré : chaque action listée correspond à une affordance réelle.
  // Aucune boutique ne s'importe : `import` a été retiré de toutes les ressources du module.
  'stores.store-list.store': ['read', 'add', 'edit', 'delete', 'export'],
  // Écran de consultation : le BO ne crée, ne modifie ni ne supprime le stock d'une boutique (il
  // vient des livraisons et des inventaires). Seul l'export CSV s'y ajoute.
  'stores.store-stocks.stock': ['read', 'export'],
  // Un inventaire est ouvert par la cabine, jamais depuis le BO : pas de `add`. `delete` = annuler
  // l'inventaire en cours. L'export de son détail est porté par `all-inventories.history`.
  'stores.current-inventory.inventory': ['read', 'edit', 'delete'],
  // Seule l'action spéciale : le reste du cycle de vie est porté par `current-inventory.inventory`.
  'stores.current-inventory.validation': ['validate-inventory'],
  // Lot de mise en défectueux retenu par la synchro au-delà du seuil (kanban #823) : valider sort
  // définitivement les pièces du stock, refuser les laisse en place. Une seule action spéciale
  // porte les deux décisions. La lecture des lots est couverte par les droits de lecture des
  // inventaires : l'écran Inventaires les affiche ensemble.
  'stores.defective-batches.validation': ['validate-defective-batch'],
  'stores.all-inventories.history': ['read', 'edit', 'export'],
  // « Enregistrer & synchroniser » exige `edit` en plus de l'action spéciale.
  'stores.schedules.schedule': ['read', 'edit', 'sync-schedule'],
  // Les kiosks sont déclarés côté CPI : le BO ne fait que les lister et les redémarrer. `edit` sert
  // la branche back-office des routes de porte partagées avec la cabine (`/kiosk/:id/open-client`).
  // `open-door` : ouvrir la porte d'une boutique à distance et confirmer une ouverture depuis le
  // BO (routes de porte partagées avec la cabine). Geste physique, isolé de `edit` (audit du 19/09).
  'stores.kiosks.kiosk': ['read', 'edit', 'restart-kiosk', 'open-door'],
  // Les exports (CSV/PDF des cartes de la période) sont calculés côté client, mais restent un droit
  // à part entière : lire les statistiques ne donne pas le droit de les sortir de l'outil.
  'stores.statistics.statistics': ['read', 'export'],
  // Écran « Store App · tâches » : le référentiel des tâches de process d'une boutique. Les routes
  // de l'app boutique qui les consomment ne passent pas par cette ressource.
  'stores.store-tasks.task': ['read', 'add', 'edit', 'delete'],

  // 12. Comptabilité — module migré : un écran unique, en lecture seule, dont chaque bloc d'export
  // porte sa propre ressource. Aucune affordance de création/édition/suppression/import.
  'accounting.accounting.figures': ['read', 'export'],
  'accounting.accounting.balance': ['export'],
  'accounting.accounting.payments': ['export'],
  'accounting.accounting.works-council-history': ['export'],
  'accounting.accounting.warnings': ['read'],

  // 13. Qualité — module migré : deux onglets de consultation, sans création, suppression, export
  // ni import. Seul le détail d'un lot mute (blocage/déblocage et commentaire), d'où `edit` sur la
  // seule traçabilité par lot.
  // `block-lot` : bloquer / débloquer un lot (rappel produit, articles invendables sur tout le
  // réseau) ; `edit` ne couvre plus que le commentaire d'un lot (audit du 19/09).
  'quality.search-by-lot.lot-traceability': ['read', 'edit', 'block-lot'],
  'quality.search-by-article.article-traceability': ['read'],

  // 14. Contrôle panier — module migré : un écran unique, entièrement suspendu à la prise de main
  // sur la caisse. Rien n'y est lisible ni mutable sans mode support (les routes `kiosks/*` de
  // manipulation d'articles le vérifient elles-mêmes), donc le module tient en une seule action.
  // La ressource `live-control` a disparu : aucune de ses actions ne portait un droit distinct.
  'cart-control.cart-control.support-mode': ['enter-support-mode'],
  // Réintroduite par l'audit du 19/09 : consulter la liste des caisses et le panier en cours sans
  // pouvoir prendre la main (le client a coché « Voir » sur cinq rôles).
  'cart-control.cart-control.live-control': ['read'],

  // 15. Ventes journalières — module migré : un écran unique (moyennes vendues + stocks minimum
  // par jour), sans création ni suppression de ligne. Le panneau « Calcul automatique » est une
  // ressource à part : on y lit et sauvegarde les paramètres de calcul d'une catégorie, et on lance
  // le calcul — mais rien à créer, supprimer, exporter ni importer.
  'daily-sales.daily-sales.daily-sales': ['read', 'edit', 'export', 'import'],
  'daily-sales.daily-sales.auto-calculation': ['read', 'edit', 'launch-sales-calculation'],

  // 16. Vendeurs — module migré : deux onglets (vendeurs, établissements) avec le même CRUD
  // complet. L'écran n'offre ni export ni import.
  'sellers.sellers.seller': ['read', 'add', 'edit', 'delete'],
  'sellers.establishments.establishment': ['read', 'add', 'edit', 'delete'],

  // 17. Caméras — module migré : ni export ni import dans l'app. Le flux se lit (écran « Caméras »)
  // et se compose en vues personnalisées, d'où add/edit/delete sur la même ressource.
  // `start-intervention` : ouvrir un appel audio/vidéo sur la caisse depuis un flux. Lire le flux
  // ne suffit pas — l'intervention parle au client présent en boutique.
  'cameras.cameras.stream': ['read', 'add', 'edit', 'delete', 'start-intervention'],
  // Deux gestes d'exploitation isolés par l'audit du 19/09 : redémarrer le runtime caméra d'une
  // boutique (coupure du flux) et synchroniser un hôte Unifi (découverte massive de caméras).
  'cameras.cameras.config': [
    'read',
    'add',
    'edit',
    'delete',
    'restart-camera-runtime',
    'sync-cameras',
  ],

  // 18. Capteurs (pas d'export/import)
  'sensors.sensors.sensor': ['read', 'add', 'edit', 'delete'],
  'sensors.fridges.fridge': ['read', 'add', 'edit', 'delete'],

  // 19. RH & Planning — module migré : quatre sous-onglets, un par ressource. Rien ne s'importe
  // hors des fiches collaborateur (`import` retiré du planning, du pointage et des absences).
  // `publish-planning` : publier le planning, ce qui envoie un SMS à chaque collaborateur
  // (registre client point 22, audit du 19/09) — distinct du déplacement d'un créneau.
  'hr.planning.weekly-planning': ['read', 'add', 'edit', 'delete', 'export', 'publish-planning'],
  // Réglages réseau du planning (règles de rémunération nuit/week-end, seuils légaux) : globaux,
  // donc sans périmètre boutique et séparés de l'édition du planning (audit du 19/09).
  'hr.planning.settings': ['read', 'edit'],
  // Sous-onglet « Tableau de bord » RH (effectif, couverture, frais, tendances) : il n'avait aucune
  // ressource propre et s'ouvrait en OU sur trois lectures (audit du 19/09).
  'hr.dashboard.overview': ['read', 'export'],
  // La fiche elle-même ne porte que son cycle de vie et son accès : chaque champ est gouverné par
  // sa ressource de champs, d'où l'absence d'`edit` ici (spec §3.1).
  'hr.staff.collaborator': ['read', 'add', 'delete', 'export', 'import'],
  // Groupes de champs de la fiche collaborateur : chaque clé de `HR_FIELD_REGISTRY` appartient à
  // exactement une de ces ressources (plus `hr.staff.contract`). `read` donne le groupe dans la
  // réponse de détail, `edit` autorise la modification de ses champs, cible couverte avant et après.
  'hr.staff.identity': ['read', 'edit'],
  'hr.staff.position': ['read', 'edit'],
  'hr.staff.assignments': ['read', 'edit'],
  'hr.staff.contract-terms': ['read', 'edit'],
  'hr.staff.cost': ['read', 'edit'],
  'hr.staff.offer': ['read', 'edit'],
  'hr.staff.comment': ['read', 'edit'],
  'hr.staff.contract': ['read', 'add', 'edit'],
  'hr.staff.formation': ['read', 'add', 'edit', 'delete'],
  'hr.staff.availability': ['read', 'edit'],
  'hr.staff.goods': ['read', 'add', 'edit', 'delete'],
  // Un pointage se crée (tablette en boutique), se corrige via ses heures validées et s'exporte,
  // mais ne se supprime jamais : `delete` retiré.
  'hr.clocking.clocking': [
    'read',
    'add',
    'edit',
    'export',
    'validate-clocking',
    'reopen-clocking',
    'add-manual-clocking',
    'cancel-clocking',
    'correct-outside-window',
  ],
  'hr.absences.absence': ['read', 'add', 'edit', 'delete', 'export'],

  // 20. Suivi des SMS — module migré : un écran unique (tableau + compteurs + export CSV), dont la
  // relance des non-lus est une action de plus sur ces mêmes SMS, pas une ressource à part.
  'sms.sms.sent': ['read', 'export', 'resend-unread-sms'],

  // 21. Contrôle Interventions (Visio) — module migré : trois listes de consultation et un écran
  // d'administration. Les échanges naissent des appels visio et sont notés par le cron d'évaluation
  // IA : rien ne se crée, ne se supprime, ne s'exporte ni ne s'importe depuis le BO.
  'visio.yesterday-interactions.interaction': ['read'],
  'visio.exchanges-to-evaluate.exchange': ['read'],
  // La modale d'évaluation s'ouvre depuis n'importe laquelle des trois listes : la lire relève du
  // droit de lecture de la liste d'où elle est ouverte, d'où `edit` seul ici — noter l'échange,
  // valider la note IA, changer son statut de résolution.
  'visio.exchanges-to-evaluate.evaluation': ['edit'],
  'visio.overview.overview': ['read'],
  // Critères d'évaluation de l'IA et seuils de notation : une configuration unique, qui existe
  // d'office (ni création ni suppression).
  'visio.administration.administration': ['read', 'edit'],

  // 22. Foncier & travaux — module migré. La carte est le seul onglet à porter un import
  // (pivot des ouvertures) ; l'archivage n'est pas un écran, seulement une action sur un projet ;
  // aucun des quatre derniers onglets n'expose d'import ni d'export.
  'real-estate.map.map': FULL,
  'real-estate.kanban.project': ['read', 'add', 'edit', 'delete', 'export'],
  'real-estate.kanban.archive': ['edit', 'archive-real-estate-project'],
  'real-estate.tasks.task': ['read', 'add', 'edit', 'delete'],
  'real-estate.calendar.calendar': ['read', 'add', 'edit', 'delete'],
  'real-estate.contacts.contact': ['read', 'add', 'edit', 'delete'],
  'real-estate.areas-of-interest.area': ['read', 'add', 'edit', 'delete'],
  // Audit du 19/09 : documents et photos d'un projet (upload, renommage, suppression) et le
  // référentiel des pré-projets et villes sortaient tous deux de `edit` sur la carte ou le kanban.
  'real-estate.kanban.document': ['read', 'add', 'edit', 'delete'],
  'real-estate.map.pre-project': ['read', 'edit', 'delete', 'import'],

  // 23. Réseau & Boutiques — module migré
  'network.network.network-dashboard': ['read', 'export'],

  // 24. Listes de diffusion — module migré : les listes elles-mêmes sont fixées en base (aucune
  // création ni suppression de liste), l'écran ne gère que leurs destinataires et n'offre ni
  // export ni import.
  'mailing-lists.mailing-lists.mailing-list': ['read', 'add', 'edit', 'delete'],

  // 25. Administration › Rôles — module migré : liste des rôles, détail, et formulaire partagé
  // création/édition portant la matrice de permissions. Un rôle ne se supprime pas depuis le BO, ne
  // s'exporte pas et ne s'importe pas.
  'roles-admin.roles.role': ['read', 'add', 'edit'],
  // Dialogue « Assigner des utilisateurs » : remplacement complet de la liste d'admins d'un rôle,
  // d'où la seule action `edit`. Le sens inverse — attribuer un rôle depuis la fiche d'un
  // utilisateur BO — est porté par `assign-bo-role` sur `users.bo-users.user`.
  'roles-admin.roles.admin-assignment': ['edit'],

  // 26. Feature Flags — écran `/feature-flags` : la liste des flags se lit et chaque flag
  // s'active ou se désactive. Rien ne s'y crée ni ne s'y supprime depuis le BO.
  'feature-flags.feature-flags.feature-flag': ['read', 'edit'],

  // 27. Prévisions — module migré, deux écrans. Le premier, « Prévision de CA » (flag
  // `CA-forecasting`), est en lecture seule (onglets « Prévision » et « Backtest ») : ses
  // paramètres de calcul et sa sélection de boutiques ne se persistent pas — ils vivent dans l'URL
  // et le temps d'une requête —, et il n'offre aucun import. Son export Excel est assemblé dans le
  // navigateur, sans route backend, d'où `export` en plus de `read`. `edit` : basculer le réglage
  // Capping (ventes observées / demande corrigée des ruptures), réservé à quelques comptes.
  'previsions.previsions.forecast': ['read', 'edit', 'export'],
  // Audit du 19/09 : un seul `read` ouvrait huit écrans. Les corrections de ventes sur rupture, la
  // sandbox (outil interne) et le calendrier des vacances scolaires deviennent des ressources propres.
  'previsions.corrections.correction': ['read', 'edit'],
  'previsions.controle-rupture.stockout-impact': ['read'],
  'previsions.sandbox.sandbox': ['read'],
  'previsions.calendar.school-holidays': ['read'],
  // Le second, « Configurateur industriel » (flag `configurateur-industriel`), tient le référentiel
  // des cuisines centrales, leurs zones de parc et le mode de production des références. Zones et
  // modes ne créent ni ne suppriment de cuisine : ils relèvent d'`edit`, au même titre que la
  // modification d'une fiche. Ni export ni import.
  'previsions.industrial-configurator.kitchen': ['read', 'add', 'edit', 'delete'],
  // 28. Pré-autorisations — module migré : la liste des sessions d'ouverture et la relecture de
  // leurs enregistrements vidéo se lisent ; `edit` couvre la seule mutation de l'écran, conserver
  // le montant pré-autorisé. Une session ne se crée, ne se supprime, ne s'exporte ni ne s'importe
  // depuis le BO.
  // `edit` = conserver (prélever) le montant pré-autorisé ; `release-pre-authorization` = libérer
  // l'empreinte bancaire. Deux gestes financiers opposés, deux droits (audit du 19/09).
  'pre-authorizations.pre-authorizations.session': ['read', 'edit', 'release-pre-authorization'],
  // 29. Agences d'intérim — module migré : un écran unique, référentiel des agences alimentant le
  // formulaire de contrat d'une fiche collaborateur. L'archivage est le seul retrait possible
  // (soft-delete), le désarchivage relève de `edit`. Ni export ni import.
  'interim-agencies.interim-agencies.agency': ['read', 'add', 'edit', 'delete'],
  // 31. Approvisionnement — écran `/supply`. La frise se lit en vue réseau comme en vue boutique
  // sous le même tuple : c'est le périmètre d'affectation qui distingue les deux. Les corrections
  // de prévision d'appro sont portées à part, elles seules se modifient.
  'supply.supply.timeline': ['read'],
  'supply.supply.correction': ['read', 'edit'],
  // 32. Outils staging — écran `/staging-tools` : compte de test d'un environnement hors
  // production (consultation, bascule, QR code). La garde d'environnement reste en place.
  'staging-tools.staging-tools.test-account': ['read', 'edit'],

  // 33. Outils d'administration — audit du 19/09 : déclencher à la main une tâche planifiée
  // (`POST /cron/*`, `POST /sync/*`) depuis une session BO exigeait le bit SUPER_ADMIN, inerte en v2.
  'admin-tools.jobs.job': ['run-job'],

  // 34. Assistant IA — audit du 19/09 : le proxy `/ai/*` n'avait aucune permission attribuable.
  'ai.assistant.assistant': ['read'],
  // 35. Logistique — référentiels des moyens et règles du module (flag `logistique`).
  // Véhicules, hubs et transporteurs sont de purs référentiels : CRUD complet, aucune donnée
  // sensible, ni export ni import d'écran.
  'logistics.referentiels.vehicle': ['read', 'add', 'edit', 'delete'],
  'logistics.referentiels.hub': ['read', 'add', 'edit', 'delete'],
  'logistics.referentiels.carrier': ['read', 'add', 'edit', 'delete'],
  // Le chauffeur porte les seules données personnelles du module, d'où deux actions en plus du
  // CRUD : `activate-driver` engage sa mise en service, `read-driver-document` ouvre ses pièces
  // justificatives — lire la fiche ne donne jamais accès aux pièces.
  //
  // PAS de `delete`, et ne pas le « rétablir » : un profil chauffeur ne se supprime pas. C'est
  // l'extension 1-1 d'un `Collaborator` RH, dont l'historique de vérification des pièces fonde des
  // activations et des calculs déjà produits, et le vocabulaire d'audit figé (`auditActions.ts`)
  // lui donne `created/updated/activated/deactivated` là où il donne `deleted` au véhicule, au hub
  // et au transporteur — exclusion consciente, pas oubli. Une action listée ici sans point
  // d'application n'accorde rien tout en étant cochable dans la matrice des rôles.
  'logistics.referentiels.driver': [
    'read',
    'add',
    'edit',
    'activate-driver',
    'read-driver-document',
  ],
  // Le profil logistique d'une boutique existe d'office avec la boutique : il se lit et se règle,
  // il ne se crée ni ne se supprime depuis ce module.
  'logistics.referentiels.store-profile': ['read', 'edit'],
  // Fiche collaborateur logistique (onglet RH) : identité, rôle, statut et rattachement d'un
  // collaborateur du référentiel RH. Ce droit ouvre la fiche et son rattachement ; chaque champ RH
  // reste soumis en plus aux droits RH de l'admin sur ce collaborateur (`hr.staff.*`), comme sur la
  // fiche RH. Pas de `add` : la fiche vierge (`POST /logistics/collaborators`) exige `edit` ici et
  // les droits de la création RH (`hr.staff.collaborator: add`, puis `edit` par groupe de champs).
  'logistics.referentiels.collaborator': ['read', 'edit'],
  // Simulation de tournées : le jeu d'entrées se gère en CRUD complet, et son exécution est un
  // geste à part (`launch-simulation`) — consulter une simulation ne donne jamais le droit de
  // mobiliser le solveur.
  'logistics.simulation.simulation': ['read', 'add', 'edit', 'delete', 'launch-simulation'],
  // La tournée n'est pas saisie à la main : elle se matérialise depuis un scénario. D'où ni `add`
  // ni `delete` ; elle se retouche (`edit`) et porte une action en plus — `validate-tour`, qui
  // engage son entrée en vigueur.
  'logistics.simulation.tour': ['read', 'edit', 'validate-tour'],
  // Onglet Product Management, servi par le service `apps/product-management` et non par une route
  // de Lucie (ADR 0010 de ce service, M13) : il lit ces deux permissions dans
  // `GET /rbac/me/permissions`. Ce sont les deux rôles de la page, pas deux écrans : `read` veut dire
  // « a ce rôle » ; le lecteur lit, l'administrateur lit et désigne les contributeurs des briques.
  // Accordées à la main dans l'écran des rôles. Le rôle « Full Access » donne le lecteur (il porte
  // la taxonomie, `rbac:sync-full-access` à chaque déploiement), jamais l'administrateur : celui-ci
  // est dans `RBAC_V2_FULL_ACCESS_EXCLUDED` (accord de Baptiste du 30/10/2026) et se donne à des
  // personnes nommées. Aucune migration ne les accorde.
  // Aucune garde de l'API de Lucie ne les vérifie (le back-office ne les lit que pour montrer
  // l'onglet, route `/product-management`) : la revue des habilitations les montrera toujours comme
  // inutilisées. Le manager et les contributeurs d'une brique ne sont pas une permission : le service
  // les tient dans sa base. `product-management.roles.contributor`, l'ancien rôle remplacé par le
  // lecteur (M13), n'est plus lu : ses lignes restées en base ne donnent aucun droit.
  'product-management.roles.reader': ['read'],
  'product-management.roles.administrator': ['read'],
  // 37. Service client — boîte de réception des tickets du support (flag `service-client-sidebar`).
  // `read` = voir la liste et le détail des tickets ; `edit` = les traiter : modifier un ticket
  // (statut, priorité, lu ou non), l'assigner, fusionner deux tickets et les actions groupées. Seuls
  // les détenteurs d'`edit` sont des agents assignables. Rien ne se crée ni ne se supprime depuis le
  // BO (les tickets viennent des canaux), ni ne s'exporte.
  'service-client.tickets.ticket': ['read', 'edit'],
} as const satisfies Record<string, readonly RbacV2Action[]>

export type RbacV2Resource = keyof typeof rbacV2Abilities

/**
 * Union de tuples `[action, ressource]` dérivée de `rbacV2Abilities`.
 * Prête pour typer un `MongoAbility<RbacV2AbilityTuple>` si l'enforcement v2 est câblé plus tard.
 */
export type RbacV2AbilityTuple = {
  [K in RbacV2Resource]: [(typeof rbacV2Abilities)[K][number], K]
}[RbacV2Resource]

export const rbacV2Resources = Object.keys(rbacV2Abilities) as RbacV2Resource[]

/** Tous les couples (ressource, action) de la taxonomie, dans l'ordre du registre. */
export const allRbacV2Permissions = (): { resource: RbacV2Resource; action: RbacV2Action }[] =>
  (Object.entries(rbacV2Abilities) as [RbacV2Resource, readonly RbacV2Action[]][]).flatMap(
    ([resource, actions]) => actions.map((action) => ({ resource, action })),
  )

/**
 * Couples tenus hors du rôle « Full Access » : des rôles métier d'une page, donnés à des personnes
 * nommées, et non des pouvoirs d'administration de Lucie (accord de Baptiste du 30/10/2026). La
 * synchro de chaque déploiement les retire au rôle (`rbac:sync-full-access`), comme le seed db-sync.
 * L'administrateur de l'onglet Product Management en est ; son lecteur reste dans Full Access.
 */
export const RBAC_V2_FULL_ACCESS_EXCLUDED = [
  ['read', 'product-management.roles.administrator'],
] as const satisfies readonly RbacV2AbilityTuple[]

export const isExcludedFromFullAccess = (resource: string, action: string): boolean =>
  RBAC_V2_FULL_ACCESS_EXCLUDED.some(
    ([excludedAction, excludedResource]) =>
      excludedResource === resource && excludedAction === action,
  )

/**
 * Ce que porte le rôle « Full Access » (`rbac:sync-full-access`), sans périmètre ni population :
 * toute la taxonomie sauf `RBAC_V2_FULL_ACCESS_EXCLUDED`, dans l'ordre du registre.
 */
export const fullAccessRbacV2Permissions = (): {
  resource: RbacV2Resource
  action: RbacV2Action
}[] =>
  allRbacV2Permissions().filter(
    ({ resource, action }) => !isExcludedFromFullAccess(resource, action),
  )

/** Libellés des couples exclus de Full Access, pour les textes qui les nomment (refus, aide). */
export const fullAccessExcludedLabels = (): string =>
  RBAC_V2_FULL_ACCESS_EXCLUDED.map(
    ([, resource]) => `« ${RBAC_V2_FEATURE_LABELS[resource]} »`,
  ).join(', ')

// --- Liaison boutique par ressource -----------------------------------------------------------

/**
 * Manière dont une ressource se rattache à une boutique, donc manière dont le périmètre d'une
 * affectation s'y applique :
 * - `record` : chaque enregistrement porte une boutique (directe ou par relation). Le serveur
 *   filtre la colonne avant pagination et agrégation, et contrôle la cible avant toute écriture.
 * - `selection` : l'écran agrège un ensemble de boutiques choisi par l'utilisateur. L'ensemble
 *   demandé doit être inclus dans l'ensemble autorisé ; sans sélection, le défaut est l'autorisé.
 * - `none` : référentiel sans boutique. Le périmètre est ignoré et l'éditeur de rôles l'affiche.
 */
export type RbacV2StoreBinding = 'record' | 'selection' | 'none'

export const RBAC_V2_STORE_BINDING_LABELS: Record<RbacV2StoreBinding, string> = {
  record: 'Par enregistrement',
  selection: 'Par sélection de boutiques',
  none: 'Sans effet',
}

/** Mode de liaison boutique de chaque ressource — source unique côté serveur et côté BO. */
export const RBAC_V2_STORE_BINDING = {
  'dashboard.dashboard.global-indicators': 'selection',
  'today.today.daily-tracking': 'record',
  'carts.carts.cart': 'record',
  'carts.vision-checks.check': 'record',
  'carts.cpi-tests.test': 'none',
  'categories.categories.standard': 'none',
  'categories.internal-categories.internal': 'selection',
  'categories.reappro.category': 'none',
  'categories.reappro.config': 'record',
  'categories.reappro.simulation': 'selection',
  'categories.reappro.adjustment': 'record',
  'categories.reappro.algorithm': 'none',
  'categories.reappro.forecast-pilot': 'none',
  'categories.brands.brand': 'none',
  'products.products.product': 'selection',
  'platform-stock.platform-stock.stock': 'none',
  'users.app-users.user': 'none',
  'users.app-users.ban': 'none',
  'users.app-users.solvency': 'none',
  'users.payment-defaults.default': 'none',
  'users.payment-links.link': 'none',
  'users.bo-users.user': 'none',
  'users.bo-users.api-key': 'none',
  'promo.temporary-campaigns.campaign': 'selection',
  'promo.temporary-campaigns.single-use-codes': 'selection',
  'works-council.works-council.partnership': 'none',
  'notifications.notifications.notification': 'selection',
  'notifications.notifications.duplication': 'selection',
  'stores.store-list.store': 'record',
  'stores.store-stocks.stock': 'record',
  'stores.current-inventory.inventory': 'record',
  'stores.current-inventory.validation': 'record',
  'stores.defective-batches.validation': 'record',
  'stores.all-inventories.history': 'record',
  'stores.schedules.schedule': 'record',
  'stores.kiosks.kiosk': 'record',
  'stores.statistics.statistics': 'record',
  'stores.store-tasks.task': 'record',
  'accounting.accounting.figures': 'selection',
  'accounting.accounting.balance': 'selection',
  'accounting.accounting.payments': 'record',
  'accounting.accounting.works-council-history': 'none',
  'accounting.accounting.warnings': 'selection',
  'quality.search-by-lot.lot-traceability': 'record',
  'quality.search-by-article.article-traceability': 'record',
  'cart-control.cart-control.support-mode': 'record',
  'cart-control.cart-control.live-control': 'record',
  'daily-sales.daily-sales.daily-sales': 'record',
  'daily-sales.daily-sales.auto-calculation': 'record',
  'sellers.sellers.seller': 'none',
  'sellers.establishments.establishment': 'none',
  'cameras.cameras.stream': 'record',
  'cameras.cameras.config': 'record',
  'sensors.sensors.sensor': 'record',
  'sensors.fridges.fridge': 'record',
  'hr.planning.weekly-planning': 'record',
  'hr.staff.collaborator': 'record',
  'hr.staff.identity': 'record',
  'hr.staff.position': 'record',
  'hr.staff.assignments': 'record',
  'hr.staff.contract-terms': 'record',
  'hr.staff.cost': 'record',
  'hr.staff.offer': 'record',
  'hr.staff.comment': 'record',
  'hr.staff.contract': 'record',
  'hr.staff.formation': 'record',
  'hr.staff.availability': 'record',
  'hr.staff.goods': 'record',
  'hr.clocking.clocking': 'record',
  'hr.planning.settings': 'none',
  'hr.dashboard.overview': 'selection',
  'hr.absences.absence': 'record',
  'sms.sms.sent': 'record',
  'visio.yesterday-interactions.interaction': 'record',
  'visio.exchanges-to-evaluate.exchange': 'record',
  'visio.exchanges-to-evaluate.evaluation': 'record',
  'visio.overview.overview': 'selection',
  'visio.administration.administration': 'none',
  'real-estate.map.map': 'none',
  'real-estate.kanban.project': 'none',
  'real-estate.kanban.archive': 'none',
  'real-estate.tasks.task': 'none',
  'real-estate.calendar.calendar': 'none',
  'real-estate.contacts.contact': 'none',
  'real-estate.areas-of-interest.area': 'none',
  'real-estate.kanban.document': 'none',
  'real-estate.map.pre-project': 'none',
  'network.network.network-dashboard': 'selection',
  'mailing-lists.mailing-lists.mailing-list': 'none',
  'roles-admin.roles.role': 'none',
  'roles-admin.roles.admin-assignment': 'none',
  'feature-flags.feature-flags.feature-flag': 'none',
  'previsions.previsions.forecast': 'selection',
  'previsions.industrial-configurator.kitchen': 'selection',
  'previsions.corrections.correction': 'selection',
  'previsions.controle-rupture.stockout-impact': 'selection',
  'previsions.sandbox.sandbox': 'selection',
  'previsions.calendar.school-holidays': 'none',
  'pre-authorizations.pre-authorizations.session': 'record',
  'interim-agencies.interim-agencies.agency': 'none',
  'supply.supply.timeline': 'selection',
  'supply.supply.correction': 'record',
  'staging-tools.staging-tools.test-account': 'none',
  'admin-tools.jobs.job': 'none',
  'ai.assistant.assistant': 'none',
  // 35. Logistique — aucune liaison boutique : le module se pilote au niveau du réseau, et ses
  // contrôleurs ne consultent pas le périmètre boutique de l'admin. Le profil logistique d'une
  // boutique passera en `record` le jour où le module honorera ce périmètre.
  'logistics.referentiels.vehicle': 'none',
  'logistics.referentiels.hub': 'none',
  'logistics.referentiels.carrier': 'none',
  'logistics.referentiels.driver': 'none',
  'logistics.referentiels.store-profile': 'none',
  'logistics.referentiels.collaborator': 'none',
  'logistics.simulation.simulation': 'none',
  'logistics.simulation.tour': 'none',
  'product-management.roles.reader': 'none',
  'product-management.roles.administrator': 'none',
  // 37. Service client — un ticket porte sa boutique (`store_id`, facultative) : seule une
  // affectation sur toutes les boutiques couvre les tickets sans boutique.
  'service-client.tickets.ticket': 'record',
} as const satisfies Record<RbacV2Resource, RbacV2StoreBinding>

/** Mode de liaison boutique d'une ressource ; `none` pour une clé inconnue (bord de lecture). */
export const getRbacV2StoreBinding = (resource: string): RbacV2StoreBinding =>
  (RBAC_V2_STORE_BINDING as Record<string, RbacV2StoreBinding>)[resource] ?? 'none'

// --- Population RH ----------------------------------------------------------------------------

/**
 * Ressources dont chaque permission porte deux dimensions de population (postes ciblés et types de
 * contrat). La cible d'une opération y est toujours un collaborateur.
 */
export const HR_POPULATION_RESOURCES = [
  'hr.staff.collaborator',
  'hr.staff.identity',
  'hr.staff.position',
  'hr.staff.assignments',
  'hr.staff.contract-terms',
  'hr.staff.cost',
  'hr.staff.offer',
  'hr.staff.comment',
  'hr.staff.contract',
  'hr.staff.formation',
  'hr.staff.availability',
  'hr.staff.goods',
  'hr.planning.weekly-planning',
  'hr.clocking.clocking',
  'hr.absences.absence',
] as const satisfies readonly RbacV2Resource[]

export type HrPopulationResource = (typeof HR_POPULATION_RESOURCES)[number]

const HR_POPULATION_RESOURCE_SET = new Set<string>(HR_POPULATION_RESOURCES)

/** Une population n'est autorisée que sur les ressources RH ciblées. */
export const isHrPopulationResource = (resource: string): resource is HrPopulationResource =>
  HR_POPULATION_RESOURCE_SET.has(resource)

// --- Périmètres (scopes) ----------------------------------------------------------------------

/**
 * Périmètre d'une permission : `global` par défaut, et le seul « Prez projet » du foncier pour les
 * couples déclarés dans `RBAC_V2_SCOPED_PERMISSIONS`.
 *
 * Une ressource RH n'a pas de périmètre déclaré : le « sur qui » est porté par la population
 * (`HrPopulation`) de la permission et le « où » par le périmètre boutique de l'affectation.
 */
export const RBAC_V2_SCOPES = ['global', 'real-estate-restricted'] as const
export type RbacV2Scope = (typeof RBAC_V2_SCOPES)[number]

export const RBAC_V2_SCOPE_LABELS: Record<RbacV2Scope, string> = {
  global: 'Global',
  'real-estate-restricted': 'Prez projet',
}

const GLOBAL_ONLY = ['global'] as const satisfies readonly RbacV2Scope[]
const REAL_ESTATE_SCOPES = [
  'global',
  'real-estate-restricted',
] as const satisfies readonly RbacV2Scope[]

/**
 * Permissions à périmètre : seul le foncier conserve son périmètre v1 « Prez projet », et seules
 * ses écritures listées sont restreintes ; les autres actions restent globales. Les ressources RH
 * n'ont plus de scope : leur périmètre boutique est porté par l'affectation du rôle et leur
 * population par `population` (modèle canonique du 18 septembre 2026).
 */
export const RBAC_V2_SCOPED_PERMISSIONS = {
  'real-estate.map.map': { actions: ['add', 'edit', 'delete'], scopes: REAL_ESTATE_SCOPES },
  'real-estate.kanban.project': { actions: ['add', 'edit', 'delete'], scopes: REAL_ESTATE_SCOPES },
  'real-estate.kanban.archive': {
    actions: ['edit', 'archive-real-estate-project'],
    scopes: REAL_ESTATE_SCOPES,
  },
  'real-estate.kanban.document': { actions: ['add', 'edit', 'delete'], scopes: REAL_ESTATE_SCOPES },
} as const satisfies Partial<
  Record<RbacV2Resource, { actions: readonly RbacV2Action[]; scopes: readonly RbacV2Scope[] }>
>

/** Ressources dont les permissions portent un périmètre explicite — source unique côté enforcement. */
export const RBAC_V2_SCOPED_RESOURCES = Object.keys(
  RBAC_V2_SCOPED_PERMISSIONS,
) as (keyof typeof RBAC_V2_SCOPED_PERMISSIONS)[]

/** Périmètres proposés pour un couple ; hors foncier, seul `global` existe. */
export const getRbacV2Scopes = (resource: string, action: string): readonly RbacV2Scope[] => {
  const entry = (
    RBAC_V2_SCOPED_PERMISSIONS as Record<
      string,
      { actions: readonly string[]; scopes: readonly RbacV2Scope[] } | undefined
    >
  )[resource]

  if (!entry) return GLOBAL_ONLY
  if (entry.actions.includes(action)) return entry.scopes
  return GLOBAL_ONLY
}

/** Un couple non déclaré à périmètre n'accepte que `global` (bord d'écriture). */
export const isRbacV2ScopeAllowed = (resource: string, action: string, scope: string): boolean =>
  (getRbacV2Scopes(resource, action) as readonly string[]).includes(scope)

/** Valide qu'une action est bien autorisée pour une ressource donnée (bord d'écriture). */
export const isRbacV2ActionAllowed = (resource: string, action: string): boolean =>
  (rbacV2Abilities as Record<string, readonly string[]>)[resource]?.includes(action) ?? false

/** Renvoie toutes les actions spéciales portées par une ressource, dans l'ordre de la taxonomie. */
export const getRbacV2SpecialActions = (resource: RbacV2Resource): RbacV2SpecialAction[] =>
  (rbacV2Abilities[resource] as readonly RbacV2Action[]).filter(
    (action): action is RbacV2SpecialAction =>
      (RBAC_V2_SPECIAL_ACTIONS as readonly string[]).includes(action),
  )

// --- Libellés (français) — maps plats, ordre de déclaration = ordre d'affichage --------------

/** Clé = segment `module` de la clé de ressource. */
export const RBAC_V2_MODULE_LABELS: Record<string, string> = {
  dashboard: '1. Tableau de bord',
  today: "2. Aujourd'hui",
  carts: '3. Paniers',
  categories: '4. Catégories',
  products: '5. Produits',
  'platform-stock': '6. Stock Plateforme',
  users: '7. Utilisateurs',
  promo: '8. Campagne promo',
  'works-council': '9. Partenariats CE',
  notifications: '10. Notifications',
  stores: '11. Boutiques',
  accounting: '12. Comptabilité',
  quality: '13. Qualité',
  'cart-control': '14. Contrôle panier',
  'daily-sales': '15. Ventes journalières',
  sellers: '16. Vendeurs',
  cameras: '17. Caméras',
  sensors: '18. Capteurs',
  hr: '19. RH & Planning',
  sms: '20. Suivi des SMS',
  visio: '21. Contrôle Interventions (Visio)',
  'real-estate': '22. Foncier & travaux',
  network: '23. Réseau & Boutiques',
  'mailing-lists': '24. Listes de diffusion',
  'roles-admin': '25. Administration › Rôles',
  'feature-flags': '26. Feature Flags',
  previsions: '27. Prévisions',
  'pre-authorizations': '28. Pré-autorisations',
  'interim-agencies': "29. Agences d'intérim",
  supply: '31. Approvisionnement',
  'staging-tools': '32. Outils staging',
  'admin-tools': "33. Outils d'administration",
  ai: '34. Assistant IA',
  logistics: '35. Logistique',
  'product-management': '36. Product Management',
  'service-client': '37. Service client',
}

/** Clé = `module.sous-onglet`. */
export const RBAC_V2_SUBTAB_LABELS: Record<string, string> = {
  'dashboard.dashboard': 'Tableau de bord',
  'today.today': "Aujourd'hui",
  'carts.carts': 'Paniers',
  'carts.vision-checks': 'Paniers › Vérification visuelle',
  'carts.cpi-tests': 'Paniers › Tests CPI',
  'categories.categories': 'Catégories',
  'categories.internal-categories': 'Catégories internes',
  'categories.reappro': 'Catégories réappro',
  'categories.brands': 'Marques',
  'products.products': 'Produits',
  'platform-stock.platform-stock': 'Stock Plateforme',
  'users.app-users': "Utilisateurs de l'app",
  'users.payment-defaults': 'Défauts de paiement',
  'users.payment-links': 'Liens de paiement',
  'users.bo-users': 'Utilisateurs du BO',
  'promo.temporary-campaigns': 'Campagnes temporaires',
  'works-council.works-council': 'Partenariats CE',
  'notifications.notifications': 'Notifications',
  'stores.store-list': 'Liste boutiques',
  'stores.store-stocks': 'Boutique › Stocks',
  'stores.current-inventory': 'Boutique › Inventaire en cours',
  'stores.defective-batches': 'Boutique › Lots défectueux retenus',
  'stores.all-inventories': 'Boutique › Tous les inventaires',
  'stores.schedules': 'Boutique › Gestion des horaires',
  'stores.kiosks': 'Boutique › Gestion des kiosks',
  'stores.statistics': 'Boutique › Statistiques',
  'stores.store-tasks': 'Boutique › Tâches Store App',
  'accounting.accounting': 'Comptabilité',
  'quality.search-by-lot': 'Recherche par lot',
  'quality.search-by-article': 'Recherche par article',
  'cart-control.cart-control': 'Contrôle panier',
  'daily-sales.daily-sales': 'Ventes journalières',
  'sellers.sellers': 'Vendeurs',
  'sellers.establishments': 'Établissements',
  'cameras.cameras': 'Caméras',
  'sensors.sensors': 'Capteurs',
  'sensors.fridges': 'Réfrigérateurs',
  'hr.planning': 'Planning',
  'hr.staff': 'Personnel',
  'hr.clocking': 'Pointage',
  'hr.absences': 'Calendrier Absences',
  'hr.dashboard': 'Tableau de bord RH',
  'sms.sms': 'Suivi des SMS',
  'visio.yesterday-interactions': 'Interactions de la Veille',
  'visio.exchanges-to-evaluate': 'Échanges à Évaluer',
  'visio.overview': "Vue d'ensemble",
  'visio.administration': 'Administration (Visio)',
  'real-estate.map': 'Carte',
  'real-estate.kanban': 'Kanban',
  'real-estate.tasks': 'Tâches',
  'real-estate.calendar': 'Calendrier',
  'real-estate.contacts': 'Contacts',
  'real-estate.areas-of-interest': "Zones d'intérêt",
  'network.network': 'Réseau & Boutiques',
  'mailing-lists.mailing-lists': 'Listes de diffusion',
  'roles-admin.roles': 'Rôles',
  'feature-flags.feature-flags': 'Feature Flags',
  'previsions.previsions': 'Prévisions',
  'previsions.industrial-configurator': 'Configurateur industriel',
  'previsions.corrections': 'Corrections de ruptures',
  'previsions.controle-rupture': 'Contrôle des ruptures',
  'previsions.sandbox': 'Sandbox prévisions',
  'previsions.calendar': 'Vacances scolaires',
  'pre-authorizations.pre-authorizations': 'Pré-autorisations',
  'interim-agencies.interim-agencies': "Agences d'intérim",
  'supply.supply': 'Approvisionnement',
  'staging-tools.staging-tools': 'Outils staging',
  'admin-tools.jobs': 'Tâches planifiées',
  'ai.assistant': 'Assistant',
  'logistics.referentiels': 'Référentiels logistiques',
  'logistics.simulation': 'Simulation de tournées',
  'product-management.roles': "Rôles de l'onglet",
  'service-client.tickets': 'Boîte de réception',
}

/** Clé = clé de ressource complète. */
export const RBAC_V2_FEATURE_LABELS: Record<RbacV2Resource, string> = {
  'dashboard.dashboard.global-indicators': 'Indicateurs globaux',
  'today.today.daily-tracking': 'Suivi du jour (classements ventes qté/CA)',
  'carts.carts.cart': 'Paniers (liste & détail)',
  'carts.vision-checks.check': 'Vérification visuelle des paniers',
  'carts.cpi-tests.test': 'Outillage de test CPI (EPC, panier, paiement de test)',
  'categories.categories.standard': 'Catégorie standard',
  'categories.internal-categories.internal': 'Catégorie interne',
  'categories.reappro.category': 'Catégorie de réappro',
  'categories.reappro.config': 'Configuration de réappro (stocks min)',
  'categories.reappro.simulation': 'Simulation générale de réappro',
  'categories.brands.brand': 'Marque',
  'products.products.product': 'Produit (fiche, identité, catégories)',
  'platform-stock.platform-stock.stock': 'Stock plateforme (quantités & lots)',
  'users.app-users.user': "Utilisateur de l'app",
  'users.app-users.ban': 'Bannissement utilisateur',
  'users.app-users.solvency': 'Solvabilité & moyens de paiement (Conecs)',
  'users.payment-defaults.default': 'Défaut de paiement',
  'users.payment-links.link': 'Lien de paiement (envoi, statut)',
  'users.bo-users.user': 'Utilisateur du BO',
  'users.bo-users.api-key': "Clés API d'un compte du BO",
  'promo.temporary-campaigns.campaign': 'Campagne temporaire',
  'promo.temporary-campaigns.single-use-codes': 'Codes promo à usage unique',
  'works-council.works-council.partnership': 'Partenariat CE',
  'notifications.notifications.notification': 'Notification',
  'notifications.notifications.duplication': 'Duplication de notification',
  'stores.store-list.store': 'Boutique',
  'stores.store-stocks.stock': 'Stock / valorisation / anti-gaspi boutique',
  'stores.current-inventory.inventory': 'Inventaire boutique',
  'stores.current-inventory.validation': "Validation d'inventaire",
  'stores.defective-batches.validation': "Validation d'un lot défectueux retenu",
  'stores.all-inventories.history': 'Historique des inventaires',
  'stores.schedules.schedule': 'Horaires boutique',
  'stores.kiosks.kiosk': 'Kiosk / borne',
  'stores.statistics.statistics': 'Statistiques boutique',
  'stores.store-tasks.task': 'Tâche de process boutique (Store App)',
  'accounting.accounting.figures': 'Chiffres comptables (ventes, TVA, impayés)',
  'accounting.accounting.balance': 'Bilan comptable',
  'accounting.accounting.payments': 'Paiements',
  'accounting.accounting.works-council-history': 'Historiques de CE',
  'accounting.accounting.warnings': 'Avertissements comptables',
  'quality.search-by-lot.lot-traceability': 'Traçabilité par lot',
  'quality.search-by-article.article-traceability': 'Traçabilité par article',
  'cart-control.cart-control.support-mode': 'Mode support (prise de main caisse)',
  'daily-sales.daily-sales.daily-sales': 'Ventes journalières',
  'daily-sales.daily-sales.auto-calculation': 'Calcul automatique des ventes',
  'sellers.sellers.seller': 'Vendeur',
  'sellers.establishments.establishment': 'Établissement',
  'cameras.cameras.stream': 'Flux caméras (par boutique)',
  'cameras.cameras.config': 'Configuration des caméras',
  'sensors.sensors.sensor': 'Capteur',
  'sensors.fridges.fridge': 'Réfrigérateur',
  'hr.planning.weekly-planning': 'Planning hebdomadaire (créneaux)',
  'hr.staff.collaborator': 'Fiche collaborateur',
  'hr.staff.identity': 'Identité du collaborateur (état civil, contact, photo)',
  'hr.staff.position': 'Poste, statut et rattachement hiérarchique',
  'hr.staff.assignments': "Boutiques d'affectation du collaborateur",
  'hr.staff.contract-terms': "Conditions de contrat (temps de travail, agence d'intérim)",
  'hr.staff.cost': 'Coût horaire du collaborateur',
  'hr.staff.offer': "Accès à l'offre collaborateur",
  'hr.staff.comment': 'Commentaire et statut dérogatoire de la fiche',
  'hr.staff.contract': 'Contrat collaborateur',
  'hr.staff.formation': 'Formation collaborateur',
  'hr.staff.availability': 'Disponibilités collaborateur',
  'hr.staff.goods': 'Matériel et vêtements collaborateur',
  'hr.clocking.clocking': 'Pointage',
  'hr.absences.absence': 'Absence',
  'sms.sms.sent': 'SMS envoyés',
  'visio.yesterday-interactions.interaction': 'Interactions de la veille',
  'visio.exchanges-to-evaluate.exchange': 'Échange à évaluer',
  'visio.exchanges-to-evaluate.evaluation': "Évaluation d'un échange",
  'visio.overview.overview': "Vue d'ensemble (boutique / opérateur)",
  'visio.administration.administration': 'Administration du module Visio',
  'real-estate.map.map': 'Carte foncière & indicateurs',
  'real-estate.kanban.project': 'Projet foncier (pipeline)',
  'real-estate.kanban.archive': "Archivage/reprise d'un projet",
  'real-estate.tasks.task': 'Tâche',
  'real-estate.calendar.calendar': 'Calendrier foncier',
  'real-estate.contacts.contact': 'Contact',
  'real-estate.areas-of-interest.area': "Zone d'intérêt",
  'network.network.network-dashboard': 'Tableau de bord réseau (comparaison)',
  'mailing-lists.mailing-lists.mailing-list': 'Liste de diffusion & destinataires',
  'roles-admin.roles.role': 'Rôle & permissions',
  'roles-admin.roles.admin-assignment': "Attribution d'admins à un rôle",
  'feature-flags.feature-flags.feature-flag': 'Feature flag (activation/désactivation)',
  'previsions.previsions.forecast': 'Prévision de CA (projection & backtest)',
  'previsions.industrial-configurator.kitchen':
    'Cuisine centrale, zones de parc & modes de production',
  'pre-authorizations.pre-authorizations.session':
    'Session de pré-autorisation (liste, relecture vidéo, conservation du montant)',
  'interim-agencies.interim-agencies.agency': "Agence d'intérim",
  'supply.supply.timeline': "Frise d'approvisionnement (réseau et boutique)",
  'supply.supply.correction': "Correction de prévision d'approvisionnement",
  'staging-tools.staging-tools.test-account': 'Compte de test (outils staging)',
  'categories.reappro.adjustment': 'Ajustement de besoin de réappro (réappro live)',
  'categories.reappro.algorithm': 'Paramètres réseau des algorithmes de réappro',
  'categories.reappro.forecast-pilot': 'Pilote du besoin de réappro par les prévisions',
  'cart-control.cart-control.live-control': 'Contrôle panier en direct (consultation)',
  'hr.planning.settings': 'Réglages réseau du planning (rémunération, seuils)',
  'hr.dashboard.overview': 'Tableau de bord RH (effectif, couverture, frais)',
  'real-estate.kanban.document': "Documents et photos d'un projet",
  'real-estate.map.pre-project': 'Référentiel des pré-projets et villes',
  'previsions.corrections.correction': 'Correction des ventes sur rupture',
  'previsions.controle-rupture.stockout-impact': 'Impact des ruptures sur les ventes',
  'previsions.sandbox.sandbox': 'Sandbox prévisions (outil interne)',
  'previsions.calendar.school-holidays': 'Calendrier des vacances scolaires',
  'admin-tools.jobs.job': "Déclenchement manuel d'une tâche planifiée (cron / synchronisation)",
  'ai.assistant.assistant': 'Assistant IA',
  'logistics.referentiels.vehicle': 'Véhicule',
  'logistics.referentiels.hub': 'Hub de rupture de charge',
  'logistics.referentiels.carrier': 'Transporteur',
  'logistics.referentiels.driver': 'Chauffeur (fiche, activation, pièces justificatives)',
  'logistics.referentiels.store-profile': "Profil logistique d'une boutique",
  'logistics.referentiels.collaborator': 'Collaborateur logistique (identité, rôle, rattachement)',
  'logistics.simulation.simulation': "Simulation de tournées (jeu d'entrées et lancement)",
  'logistics.simulation.tour': 'Tournée simulée ou validée',
  'product-management.roles.reader': "Rôle lecteur de l'onglet Product Management",
  'product-management.roles.administrator':
    "Rôle administrateur de l'onglet Product Management (lit et désigne les contributeurs)",
  'service-client.tickets.ticket':
    'Ticket client (traitement : assignation, fusion, actions groupées)',
}

/** Libellé de chaque action (partagée ou spéciale). */
export const RBAC_V2_ACTION_LABELS: Record<RbacV2Action, string> = {
  read: 'Voir',
  add: 'Ajouter',
  edit: 'Modifier',
  delete: 'Supprimer',
  export: 'Exporter',
  import: 'Importer',
  'launch-reappro-simulation': 'Lancer la simulation',
  'send-ban-email': 'Relancer par email',
  'assign-bo-role': 'Assigner un rôle',
  'validate-inventory': "Valider l'inventaire",
  'validate-defective-batch': 'Valider / refuser un lot défectueux',
  'sync-schedule': 'Enregistrer & synchroniser',
  'restart-kiosk': 'Redémarrer le kiosk',
  'enter-support-mode': 'Passer en mode support',
  'launch-sales-calculation': 'Lancer le calcul',
  'validate-clocking': 'Valider le pointage',
  'reopen-clocking': 'Rouvrir le pointage',
  'add-manual-clocking': 'Ajouter un pointage manuel',
  'cancel-clocking': 'Annuler le pointage',
  'correct-outside-window': 'Corriger hors délai',
  'resend-unread-sms': 'Relancer les non-lus',
  'archive-real-estate-project': 'Archiver / reprendre',
  'start-intervention': 'Démarrer une intervention',
  'edit-prices': 'Modifier les prix produit',
  'publish-planning': 'Publier le planning (envoi SMS)',
  'block-lot': 'Bloquer / débloquer un lot',
  'open-door': 'Ouvrir la porte à distance',
  'restart-camera-runtime': 'Redémarrer le runtime caméra',
  'sync-cameras': 'Synchroniser les caméras Unifi',
  'run-job': 'Déclencher la tâche',
  'reset-password': 'Réinitialiser le mot de passe',
  'issue-for-others': 'Émettre une clé pour un autre compte',
  'release-pre-authorization': "Libérer l'empreinte bancaire",
  'activate-driver': 'Activer le chauffeur',
  'read-driver-document': 'Consulter les pièces justificatives',
  'launch-simulation': 'Lancer la simulation',
  'validate-tour': 'Valider la tournée',
}

// --- DTOs (permissions de rôle v2) ------------------------------------------------------------

export interface RbacV2RolePermission {
  resource: RbacV2Resource
  action: RbacV2Action
  /** Absent = `global` ; seul le foncier déclare un autre périmètre. */
  scope?: RbacV2Scope
  /**
   * Population couverte par la permission sur une ressource de `HR_POPULATION_RESOURCES`.
   * `null` ou absent vaut « tous les postes » et « tous les types de contrat ».
   */
  population?: HrPopulation | null
  /** Provenance d'un grant chargé ; optionnelle dans les payloads d'écriture. */
  roleId?: number
  /** Provenance d'un grant chargé ; optionnelle dans les payloads d'écriture. */
  permissionId?: number
}

export interface ReplaceRbacV2RolePermissionsRequest {
  permissions: RbacV2RolePermission[]
}
